import { useEffect, useRef, useState } from "react";
import { Volume2, Pause, Play, Square, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Vorlesen mit natürlicher KI-Stimme (OpenAI TTS, serverseitig gecacht).
export const ReadAloudButton = ({ text, label = "Seite vorlesen" }) => {
  const [state, setState] = useState("idle"); // idle | loading | playing | paused
  const audioRef = useRef(null);

  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  if (!text) return null;

  const start = async () => {
    setState("loading");
    try {
      const res = await axios.post(`${API}/tts`, { text: text.slice(0, 7900) });
      const audio = new Audio(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      audio.onended = () => setState("idle");
      audio.onerror = () => setState("idle");
      audioRef.current = audio;
      await audio.play();
      setState("playing");
    } catch {
      setState("idle");
    }
  };

  const toggle = () => {
    if (state === "idle") return start();
    if (state === "loading") return;
    if (state === "playing") {
      audioRef.current?.pause();
      setState("paused");
    } else {
      audioRef.current?.play();
      setState("playing");
    }
  };

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setState("idle");
  };

  return (
    <div className="inline-flex items-center gap-2" role="group" aria-label="Vorlesefunktion">
      <button
        type="button"
        data-testid="read-aloud-btn"
        onClick={toggle}
        aria-label={state === "playing" ? "Vorlesen pausieren" : label}
        className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-white px-4 py-2 text-[13px] font-semibold text-sand-700 hover:bg-cream-100 transition-colors"
      >
        {state === "idle" && <><Volume2 className="h-4 w-4 text-terra" /> {label}</>}
        {state === "loading" && <><Loader2 className="h-4 w-4 text-terra animate-spin" /> Stimme lädt …</>}
        {state === "playing" && <><Pause className="h-4 w-4 text-terra" /> Pause</>}
        {state === "paused" && <><Play className="h-4 w-4 text-terra" /> Weiter</>}
      </button>
      {(state === "playing" || state === "paused") && (
        <button
          type="button"
          data-testid="read-aloud-stop"
          onClick={stop}
          aria-label="Vorlesen beenden"
          className="inline-flex items-center justify-center rounded-full border border-sand-300 bg-white p-2.5 text-sand-600 hover:bg-cream-100 transition-colors"
        >
          <Square className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};
